/**
 * Hook de chargement des destinations du formulaire d'inscription.
 *
 * Source de vérité unique : collection MongoDB `form_destinations`
 * (endpoint GET /api/form-destinations).
 *
 * Fournit la même API que l'ancien fichier statique `/src/data/destinations.js`
 * pour minimiser le diff dans les composants consommateurs :
 *  - destinations             : array des destinations actives
 *  - destTripMap              : mapping {dest_id -> {titleKey, startDate, dateLabel}}
 *  - isDestinationVisible(d)  : true si dateFin >= aujourd'hui
 *  - getMaxOccupantsChambre(id, group) : nombre max d'occupants par chambre
 *  - getChambreIndividuelleInfo(id, group) : { available, amount }
 *  - loaded                   : boolean
 *
 * Fallback : si le fetch échoue (réseau / backend down), retourne un array vide
 * mais ne plante pas le formulaire — il affichera un loader / un message d'erreur.
 */
import { useState, useEffect, useMemo, useCallback } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

// Cache module-level (évite les fetch en double si plusieurs composants utilisent le hook)
let _cache = null;
let _cachePromise = null;

// Iter 231 (Kamel, bug mobile) — Ne JAMAIS mettre en cache un échec : 3 tentatives avec backoff,
// et si tout échoue on laisse la possibilité de réessayer (avant : un seul échec réseau
// gravait une liste vide pour toute la session → menu déroulant vide sans message).
const fetchDestinations = async () => {
  if (_cache && _cache.length) return _cache;
  if (_cachePromise) return _cachePromise;
  _cachePromise = (async () => {
    for (let i = 0; i < 3; i++) {
      try {
        const r = await fetch(`${API}/api/form-destinations`);
        if (r.ok) {
          const data = await r.json();
          if (Array.isArray(data) && data.length > 0) {
            _cache = data;
            return _cache;
          }
        }
      } catch (err) {
        console.error(`[useDestinations] tentative ${i + 1}/3 échouée:`, err);
      }
      await new Promise(res => setTimeout(res, 800 * (i + 1)));
    }
    _cachePromise = null;
    return [];
  })();
  return _cachePromise;
};

export const resetDestinationsCache = () => { _cache = null; _cachePromise = null; };

export const isDestinationVisible = (dest, today = new Date()) => {
  if (!dest || !dest.dateFin) return true;
  const end = new Date(dest.dateFin + 'T23:59:59');
  return end >= today;
};

export const getMaxOccupantsChambre = (destId, group, destinations = null) => {
  // Si on a la destination en DB, utilise le champ persisté
  if (destinations) {
    const d = destinations.find(x => x.id === destId);
    if (d && typeof d.maxOccupantsChambre === 'number') return d.maxOccupantsChambre;
  }
  // Fallback (logique copiée de destinations.js)
  const g = (group || '').toUpperCase();
  if (!destId) return 4;
  if (destId === 'thailande' || destId === 'algerie') return 3;
  if (g.startsWith('WEEK-END MARSEILLE')) return 3;
  if (destId.startsWith('turin') || destId === 'grau-du-roi') return 0;
  return 4;
};

// Iter 854 (Kamel — Portugal renommé « PORTUGAL » après création « SUD DU PORTUGAL - ALGARVE » : lien perdu → chambre individuelle
// « plus dispo » ignorée, compteur figé). Même règle que le backend : trip_id explicite > titre ⊂ ET date = > repli tolérant
// (même date de départ + un mot de destination en commun, un seul candidat).
const _norm = (s) => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
export const findTripForDest = (trips, tm, destId = '') => {
  if (!Array.isArray(trips) || !tm) return undefined;
  const { titleKey, startDate, trip_id: tripId } = tm;
  if (tripId) { const byId = trips.find(t => t.id === tripId); if (byId) return byId; }
  const exact = trips.find(t => titleKey && t.title?.includes(titleKey) && t.start_date === startDate);
  if (exact) return exact;
  const motsFd = new Set(_norm(`${destId} ${titleKey || ''}`).split('-').filter(m => m.length > 2));
  const sameDate = trips.filter(t => startDate && (t.start_date === startDate || (t.date_options || []).some(o => (o.start_date || '').slice(0, 10) === startDate)));
  const cands = sameDate.filter(t => _norm(t.title).split('-').some(m => m.length > 2 && motsFd.has(m)));
  return cands.length === 1 ? cands[0] : undefined;
};

export const getChambreIndividuelleInfo = (destId, group, destinations = null) => {
  if (destinations) {
    const d = destinations.find(x => x.id === destId);
    if (d && d.chambreIndividuelle) return d.chambreIndividuelle;
  }
  const g = (group || '').toUpperCase();
  if (!destId) return { available: false, amount: 0 };
  if (destId.startsWith('turin') || destId === 'grau-du-roi') return { available: false, amount: 0 };
  if (destId === 'algerie') return { available: false, amount: 0 };
  if (g.startsWith('WEEK-END MARSEILLE')) return { available: false, amount: 0 };
  if (destId === 'thailande') return { available: true, amount: 490 };
  if (destId === 'new-york') return { available: true, amount: 590 };
  if (destId.startsWith('lloret-8j-')) return { available: true, amount: 220 };
  if (destId.startsWith('lloret-4j-')) return { available: true, amount: 100 };
  if (destId === 'istanbul') return { available: true, amount: 180 };
  return { available: true, amount: 0 };
};

export const useDestinations = () => {
  const [destinations, setDestinations] = useState(_cache || []);
  const [loaded, setLoaded] = useState(!!(_cache && _cache.length));

  useEffect(() => {
    let mounted = true;
    fetchDestinations().then(data => {
      if (mounted) {
        setDestinations(data);
        setLoaded(true);
      }
    });
    return () => { mounted = false; };
  }, []);

  // Iter 231 — Bouton « Réessayer » quand le chargement a échoué (mobile / réseau instable).
  const reload = useCallback(async () => {
    resetDestinationsCache();
    setLoaded(false);
    const data = await fetchDestinations();
    setDestinations(data);
    setLoaded(true);
  }, []);

  // Construit le destTripMap depuis les données DB (champ tripMap inclus)
  // Mémoïsé pour éviter de relancer les useEffect des consommateurs à chaque render.
  const destTripMap = useMemo(() => {
    const m = {};
    destinations.forEach(d => {
      if (d.tripMap) m[d.id] = d.tripMap;
    });
    return m;
  }, [destinations]);

  const memoMaxOccupants = useCallback((id, group) => getMaxOccupantsChambre(id, group, destinations), [destinations]);
  const memoChambreInd = useCallback((id, group) => getChambreIndividuelleInfo(id, group, destinations), [destinations]);

  return {
    destinations,
    destTripMap,
    loaded,
    reload,
    isDestinationVisible,
    getMaxOccupantsChambre: memoMaxOccupants,
    getChambreIndividuelleInfo: memoChambreInd,
  };
};

export default useDestinations;
